import Layout from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import {
  Compass, Strategy, ShieldCheck, ListChecks, Brain, Stack,
  Target, Path, Users, Flag, Wrench, MapTrifold, Lightbulb, ArrowRight,
} from "@phosphor-icons/react";

const FOCUS_STEPS = [
  { code: "F", title: "Fact-Find & Context", icon: Compass, body: "Establish the ground truth. Capture the internal and external environment, the current situation, and the constraints you are operating under before any decision is made." },
  { code: "O", title: "Objectives & Intent", icon: Target, body: "Separate the Why (Strategic Intent) from the What/When/Who (Operational Objectives). Intent endures even when plans change; clear objectives make success measurable." },
  { code: "C", title: "Course of Action", icon: Path, body: "Develop up to three genuinely distinct options. Forcing alternatives guards against anchoring on the first idea and exposes the real decision space." },
  { code: "U", title: "Understand Risks & Opportunities", icon: Lightbulb, body: "Stress-test each option. Weigh opportunities against operational risks, run a pre-mortem, and surface the blind spots before they surface you." },
  { code: "S", title: "Select & Set Method", icon: ShieldCheck, body: "Commit to the preferred option and pass three gatekeeper checks — Value Alignment, Objective Certainty, and Authority Baseline — so the decision is defensible and the right level owns it." },
];

const ARMOR_STEPS = [
  { code: "A", title: "Alignment & Objective", icon: Target, body: "Restate the chosen intent so every team interprets the objective identically. Alignment is the antidote to drift during execution." },
  { code: "R", tag: "R — Roles", title: "Responsibilities & Method", icon: Users, body: "Define the high-level phases, create Working Groups, and assign clear, owned tasks. No orphaned work, no overlaps." },
  { code: "M", title: "Marches & Milestones", icon: Flag, body: "Set hard deadlines, verifiable milestones, and a review cadence frequent enough to detect failure early." },
  { code: "O", title: "Operational Boundaries", icon: MapTrifold, body: "For each working group, establish their clear Operational Guardrails (what they cannot do) alongside explicit Autonomy Clearances (what they are empowered to do). This balances safety parameters with fast, decentralized decision-making." },
  { code: "R", tag: "R — Resources", title: "Resources & Enablers", icon: Wrench, body: "Identify internal resources, external expertise, and the KPIs that will track success — ideally leading indicators, not just lagging ones." },
];

const Principle = ({ icon: Icon, title, children }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon size={20} weight="duotone" className="text-blue-700" />
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    </div>
    <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
  </div>
);

const StepRow = ({ s, n, accent }) => (
  <div className="flex gap-4 items-start border border-slate-200 rounded-sm bg-white p-4">
    <div className={`w-12 h-12 shrink-0 rounded-sm flex items-center justify-center font-mono text-2xl font-semibold text-white ${accent === "amber" ? "bg-amber-600" : "bg-blue-700"}`}>
      {s.code}
    </div>
    <div>
      <div className="flex items-center gap-2">
        <span className={`font-mono text-[11px] font-bold uppercase tracking-wider ${accent === "amber" ? "text-amber-700" : "text-blue-700"}`}>Step {n}{s.tag ? ` · ${s.tag}` : ""}</span>
        <h4 className="text-base font-semibold text-slate-900">{s.title}</h4>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mt-1">{s.body}</p>
    </div>
  </div>
);

export default function Guide() {
  const navigate = useNavigate();
  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-10 px-6 lg:px-10">
        {/* Hero */}
        <div className="flex items-center gap-2 mb-3">
          <Stack size={22} weight="duotone" className="text-blue-700" />
          <span className="font-mono text-xs font-semibold tracking-[0.12em] uppercase text-slate-500">The Method</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">How &amp; Why FOCUS·ARMOR works</h1>
        <p className="mt-3 text-lg text-slate-600 leading-relaxed">
          FOCUS·ARMOR is the flagship execution framework in the <span className="font-medium text-slate-900">Operations pillar</span> of MODUS.
          It turns a battle-proven military decision model into a guided, repeatable method for planning and executing{" "}
          <span className="font-medium text-slate-900">any</span> non-routine problem — and, like every tool in the suite, it produces a
          structured, shareable executive artifact rather than a blank page.
        </p>

        {/* Why */}
        <section className="mt-10">
          <h2 className="text-xl font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">Why a shared framework?</h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            The model is drawn from military planning doctrine, where the same structured process is used
            consistently up and down the chain of command. That consistency is the point: when a junior team and
            senior leadership use the <span className="font-medium">same steps and the same language</span>, plans are faster to
            write, easier to brief, and harder to misread. The acronyms — <span className="font-mono text-blue-700">FOCUS</span> and{" "}
            <span className="font-mono text-amber-700">ARMOR</span> — are deliberate mnemonics. They are the scaffolding that lets
            anyone recognise where they are in the process at a glance.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mt-3">
            The tool keeps language deliberately generic so it applies equally to a maintenance shutdown, a product
            launch, an incident response, or a strategy. It is built on two well-established disciplines:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Principle icon={Strategy} title="Mission Command">
              Leaders set a clear intent and boundaries, then trust teams to exercise disciplined initiative within
              them. Tell people <em>what</em> and <em>why</em>, not just <em>how</em>.
            </Principle>
            <Principle icon={ShieldCheck} title="High Reliability Organisations (HRO)">
              A preoccupation with failure, reluctance to over-simplify, and sensitivity to operations — the habits
              that let high-stakes organisations operate safely under pressure.
            </Principle>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed mt-4">
            That same insistence on a shared language runs across all of MODUS. A decision framed here in Operations
            sits beside the Strategy and People frameworks in the same workspace, draws on the same Tzu advisor, and is
            filed in the same Library — so the way you plan, brief and hand over work stays consistent from pillar to pillar.
          </p>
        </section>

        {/* When to deploy */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">When to deploy FOCUS·ARMOR</h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            This framework is not for standard, routine tasks governed by existing SOPs. Deploy FOCUS·ARMOR when an
            operation meets any of the following criteria:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
            {[
              ["Non-Routine Operations", "Any task, modification, or project that sits outside standard daily operating procedures and requires custom coordination."],
              ["High-Consequence Scenarios", "Situations carrying elevated risk to personnel safety, production continuity, regulatory compliance, or critical asset integrity."],
              ["Cross-Functional Execution", "Complex problems where multiple working groups, departments, or external contractors must execute simultaneously without overlapping."],
            ].map(([t, d], i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-xs font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-sm font-semibold text-slate-900">{t}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </section>


        {/* The two phases */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">Two phases: Plan, then Execute</h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-6">
            Every assessment moves through ten steps — one per letter — split across two phases. <span className="font-mono text-blue-700 font-semibold">FOCUS</span> is
            how you <span className="font-medium">think and decide</span>. <span className="font-mono text-amber-700 font-semibold">ARMOR</span> is
            how you <span className="font-medium">organise and act</span>.
          </p>

          {/* FOCUS */}
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-xs font-bold px-2 py-1 rounded-sm bg-blue-100 text-blue-700">PHASE 1 · FOCUS — PLAN</span>
          </div>
          <div className="space-y-2.5">
            {FOCUS_STEPS.map((s, i) => <StepRow key={i} s={s} n={i + 1} accent="blue" />)}
          </div>

          {/* ARMOR */}
          <div className="flex items-center gap-2 mb-3 mt-8">
            <span className="font-mono text-xs font-bold px-2 py-1 rounded-sm bg-amber-100 text-amber-700">PHASE 2 · ARMOR — EXECUTE</span>
          </div>
          <div className="space-y-2.5">
            {ARMOR_STEPS.map((s, i) => <StepRow key={i} s={s} n={i + 6} accent="amber" />)}
          </div>
        </section>

        {/* How to use */}
        <section className="mt-12">
          <h2 className="text-xl font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">How to use the tool</h2>
          <ol className="space-y-3">
            {[
              ["Create from the Workspace", "Launch FOCUS·ARMOR from the MODUS Workspace launchpad (Operations pillar) or the sidebar. It begins life as a Draft alongside your other documents."],
              ["Work the wizard", "Move through the ten steps. Use Save Draft at any time — nothing is lost. The phase stepper shows exactly where you are."],
              ["Consult Tzu", "Open the Tzu advisor on any step for a Socratic, Red-Team critique. Tzu never writes your plan — it stress-tests your thinking and flags blind spots. You decide what to keep."],
              ["Finalise & Launch", "On the final step, launch the plan. It transitions from Draft to Active Execution and lands back on your dashboard."],
              ["Track to completion", "Open an active plan to tick off tasks by Working Group. Progress updates live, and the plan auto-completes at 100%. Export a clean copy to PDF or print at any time."],
            ].map(([t, d], i) => (
              <li key={i} className="flex gap-3 bg-white border border-slate-200 rounded-sm p-4">
                <span className="w-7 h-7 shrink-0 rounded-sm bg-slate-900 text-white font-mono text-sm flex items-center justify-center">{i + 1}</span>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">{t}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Gatekeepers callout */}
        <section className="mt-10">
          <div className="bg-slate-900 text-white rounded-sm p-6">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={20} weight="duotone" className="text-blue-400" />
              <h3 className="text-base font-semibold">The Gatekeeper checks</h3>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Before a course of action is selected, three gates must be cleared:
            </p>
            <ul className="mt-3 space-y-2">
              {[
                ["Value Alignment", "does this course of action align strictly with our foundational corporate principles?"],
                ["Objective Certainty", "is it clearly achievable?"],
                ["Authority Baseline", "can we act locally, or must this escalate?"],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-300 leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 rounded-sm bg-blue-400 shrink-0" />
                  <span><span className="font-medium text-white">{t}</span> ({d})</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              They keep decisions honest and ensure the right level of the organisation owns the call.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-slate-200 pt-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Ready to plan your next problem?</h3>
            <p className="text-sm text-slate-500">Start a structured assessment in under a minute.</p>
          </div>
          <button
            data-testid="guide-start-button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800 transition-colors"
          >
            Go to Dashboard <ArrowRight size={16} />
          </button>
        </section>
      </div>
    </Layout>
  );
}
